/**
 * The time axis: one bucket definition, shared by the histogram query and the
 * scrubber that draws it.
 *
 * Buckets are **non-uniform in years and uniform on screen**. The corpus is not
 * spread evenly across time — measured on the indexed data, 15,400 of 19,600
 * dated objects fall in 1900–2025, while the whole of 250–1000 CE holds 28. A
 * linear year axis would spend 80% of its width on 30 objects and crush the
 * rest into three pixels; equal-width buckets with widening spans give every
 * period a target you can actually hit.
 *
 * The cost is that dragging is not linear in years, which is why the scrubber
 * always shows the resolved years as text and offers exact year inputs. The
 * axis is honest because it is labelled, not because it is uniform.
 */

/**
 * Earliest year the timeline represents. Predynastic Egyptian material in the
 * corpus reaches -4000 and nothing legitimate precedes it.
 */
export const TIMELINE_MIN_YEAR = -4000;

/**
 * Latest year the timeline represents. Past this is a parse artefact, not a
 * date: the corpus holds an ArtIC record whose "2003-ongoing" became end
 * 5000001, and two SMB records whose inventory number "III C 9938" was read as
 * the year 9938.
 */
export const TIMELINE_MAX_YEAR = 2050;

/**
 * Year 0 is never a date. It does not exist in the Gregorian calendar (1 BCE is
 * followed by 1 CE), and every one of the 390 rows carrying it is a museum's
 * "unknown" sentinel copied through — Harvard's `datebegin: 0`, on records whose
 * own display string reads "1900-1943". Treating it as a real year piles them
 * onto the start of the common era.
 */
export const NOT_A_YEAR = 0;

/** A segment of the axis, and how finely it is divided. */
interface AxisSegment {
  from: number;
  to: number;
  step: number;
}

/**
 * Resolution follows density: millennia in prehistory, 25 years across the
 * period that holds three quarters of the collection.
 */
const AXIS_SEGMENTS: AxisSegment[] = [
  { from: TIMELINE_MIN_YEAR, to: -1000, step: 1000 },
  { from: -1000, to: 0, step: 500 },
  { from: 0, to: 1500, step: 500 },
  { from: 1500, to: 1800, step: 100 },
  { from: 1800, to: TIMELINE_MAX_YEAR, step: 25 },
];

export interface TimelineBucket {
  /** First year in the bucket, inclusive. */
  start: number;
  /** Last year in the bucket, inclusive. */
  end: number;
  /** Objects whose date span overlaps this bucket. */
  count: number;
}

/** The bucket edges, without counts. Stable: the same list on every call. */
export function timelineBucketRanges(): Array<{ start: number; end: number }> {
  const ranges: Array<{ start: number; end: number }> = [];
  for (const segment of AXIS_SEGMENTS) {
    for (let year = segment.from; year < segment.to; year += segment.step) {
      // `end` is inclusive, so a bucket runs up to the year before the next
      // one starts. Without that the boundary year would be counted twice.
      ranges.push({ start: year, end: Math.min(year + segment.step, segment.to) - 1 });
    }
  }
  return ranges;
}

/** "1850", "500 BCE". Year 1 BCE is the year before 1 CE, so -1 renders as 1 BCE. */
export function formatYear(year: number): string {
  if (year < 0) return `${Math.abs(year)} BCE`;
  return String(year);
}

/**
 * A bucket's label for a reader. Single-year buckets never occur with the
 * current segments, but the case is handled so a finer step does not produce
 * "1850–1850".
 */
export function formatBucketLabel(bucket: { start: number; end: number }): string {
  if (bucket.start === bucket.end) return formatYear(bucket.start);
  // A range that crosses zero reads better with both eras named; one that does
  // not can say the era once, at the end.
  if (bucket.start < 0 && bucket.end >= 0) {
    return `${formatYear(bucket.start)} – ${formatYear(bucket.end)}`;
  }
  if (bucket.start < 0) {
    return `${Math.abs(bucket.start)}–${formatYear(bucket.end)}`;
  }
  return `${bucket.start}–${bucket.end}`;
}

/**
 * A year short enough for an axis tick: "1850", "4000B". The trailing B marks
 * BCE, because a minus sign on a tick reads as a negative count rather than an
 * era, and "4000 BCE" is too wide for a 25px column.
 */
export function formatYearShort(year: number): string {
  return year < 0 ? `${Math.abs(year)}B` : String(year);
}

/** Clamp a year to the representable range, for input sanitising. */
export function clampYear(year: number): number {
  return Math.min(TIMELINE_MAX_YEAR, Math.max(TIMELINE_MIN_YEAR, Math.round(year)));
}

/**
 * Whether a range covers the whole axis, i.e. filters nothing. Callers use this
 * to leave the date predicate out of a query rather than emit one that matches
 * everything.
 */
export function isFullRange(from: number, to: number): boolean {
  return from <= TIMELINE_MIN_YEAR && to >= TIMELINE_MAX_YEAR;
}

/**
 * Index of the bucket containing a year, or the nearest one when the year sits
 * outside the axis. Used to place the scrubber handles.
 */
export function bucketIndexForYear(year: number): number {
  const ranges = timelineBucketRanges();
  if (year <= ranges[0].start) return 0;
  const last = ranges.length - 1;
  if (year >= ranges[last].end) return last;
  const found = ranges.findIndex((r) => year >= r.start && year <= r.end);
  return found === -1 ? last : found;
}
